/**
 * Press Box → Games. 2026-10-17.
 *
 * Every game of the season with its score and where its stats came from:
 * LIVE (being recorded), Analysing (the after-game pass is running), Official
 * · AI-checked (rules + the model on the GPU box), Official · rules only, or
 * Failed. Each row uploads a recording straight into that game; "Add a game"
 * creates one first (for recordings of games that were never on the
 * schedule, e.g. the Sep 18 – Oct 11 tapes).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Upload, Mic, ChevronRight, Plus, Sparkles, AlertTriangle, Loader2, Users, MoreVertical, Trash2, MapPin, Pencil, CheckCircle2, Square, RefreshCw, FileCheck2, FileAudio, CalendarClock, Clapperboard } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { chatSocket } from '../../services/socket.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { uploadRecording, uploadError, RECORDING_ACCEPT, fileSha256, activeUploads } from '../../utils/recordingUpload.js';
import PressBoxShell, { keepsStats } from './PressBoxShell.jsx';
import { PRESS_BOX, modelAlias } from '../../constants/pressBox.js';
import { fmtGameTime, phaseOf, PHASE_LABELS, mapsUrl } from '../../utils/pressBoxFormat.js';
import { uploadScoreSheet, SHEET_ACCEPT } from './ScoreSheetCard.jsx';
import { uploadGameVideo, GAME_VIDEO_ACCEPT, probeFile } from '../../utils/recordingUpload.js';

// one picker for everything the narrator brings home from a game
const ANY_ACCEPT = 'audio/*,video/*,image/*,.m4a,.mp3,.wav,.webm,.ogg,.aac,.opus,.mp4,.mov,.mkv,.m4v,.3gp,.png,.jpg,.jpeg';

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

/** Recording states that mean "the stats are still being worked out". */
export const IN_PROGRESS = ['batch', 'finishing', 'storing', 'extracting', 'live', 'live_final', 'stt_wait'];

export function StatusChip({ game }) {
  const r = game.recording;
  // Token colours only (no color-mix): the native apps run on old WebViews.
  const chip = (text, tone, title) => (
    <span className="badge" title={title} style={tone === 'muted'
      ? { background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }
      : { background: `var(--${tone}-a10)`, color: `var(--${tone})`, border: `1px solid var(--${tone}-a20)` }}>{text}</span>
  );
  if (game.live) return chip('● Live', 'penalty', 'Being recorded right now');
  if (!r) return game.events ? chip('Entered by hand', 'muted') : null;
  if (r.engine === 'stt_wait')
    return chip('Waiting for GPU…', 'puck', 'Press Box audio is always transcribed by Whisper on the GPU — this pass starts the moment a card is free (checked every few minutes)');
  if (IN_PROGRESS.includes(r.engine))
    return chip('Analysing…', 'ice', 'The after-game pass is running');
  if (r.engine === 'batch_error' || r.engine === 'error') return chip('Failed', 'penalty', r.error || 'The after-game pass failed');
  if (r.engine === 'final') {
    const a = r.analysis;
    // 2026-10-21 (per Gilles) — a game the AI has not fully read but with a
    // next look booked is QUEUED, not stuck: it finishes by itself.
    const incomplete = a?.requested === 'ai' && (a.mode !== 'rules+llm' || a.interrupted || a.fallbackWindows > 0);
    if (incomplete && r.aiHold)
      return chip('On hold', 'muted', `Taken out of the AI queue — it won't go to the GPU until someone presses Re-run ${PRESS_BOX.AI_NAME} on the game`);
    if (incomplete && r.aiQueued)
      return chip('Queued for AI…', 'ice', `In line for ${PRESS_BOX.AI_NAME} — first come, first served; it finishes by itself the moment the GPU frees up${r.aiRetryAt ? ` (next look about ${new Date(r.aiRetryAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })})` : ''}`);
    if (a?.mode === 'rules+llm' && a.interrupted) return chip(`${PRESS_BOX.AI_NAME} (part)`, 'puck', `Checked by ${modelAlias(a.model) || PRESS_BOX.AI_NAME} until it stopped: ${a.interrupted}. The rest is the rules.`);
    if (a?.mode === 'rules+llm') return chip(`${PRESS_BOX.AI_NAME} ✓`, 'goal', `Official · checked by ${modelAlias(a.model) || PRESS_BOX.AI_NAME}`);
    if (a?.requested === 'ai') return chip(`${PRESS_BOX.AI_NAME} not run`, 'puck', `${PRESS_BOX.AI_NAME} has not read this transcript: ${a.error || 'no answer'}`);
    return chip('Official', 'goal', `Official pass (${PRESS_BOX.AI_NAME} not requested)`);
  }
  return null;
}

/**
 * 2026-10-19 — the ⋯ menu on each schedule row. Today: delete the game
 * (stats and recording go with it, after a confirm). More items later.
 */
function GameMenu({ teamId, game, onDeleted, onChanged }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const del = async () => {
    setOpen(false);
    const what = `${game.date} vs ${game.opponent || 'TBA'}`;
    if (!window.confirm(`Delete ${what}?\n\nIts stats${game.recording ? ' and its recording' : ''} are deleted with it. This cannot be undone.`)) return;
    try {
      await api.delete(`/teams/${teamId}/press-box/games/${game.id}`);
      toast.success('Game deleted');
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not delete the game');
    }
  };
  // 2026-10-23 (per Gilles) — the Halton game sat on the wrong date: change
  // the date/time by hand. A scheduled game that day without a recording
  // takes this game's recording + stats over (and the stand-in disappears);
  // otherwise the game is simply re-dated.
  const changeDate = async () => {
    setOpen(false);
    const date = window.prompt('New date (YYYY-MM-DD)?', game.date || '');
    if (date == null) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) { toast.error('Use YYYY-MM-DD'); return; }
    const time0 = window.prompt('Start time (HH:MM, 24h) — leave empty to keep it', (game.time || '').slice(0, 5));
    if (time0 == null) return;
    const time = time0.trim();
    if (time && !/^\d{2}:\d{2}$/.test(time)) { toast.error('Use HH:MM (24h)'); return; }
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/games/${game.id}/redate`, { date: date.trim(), ...(time ? { time } : {}) });
      toast.success(data.moved
        ? `The schedule already had a game that day — the recording moved onto it (vs ${data.opponent || 'TBA'}${data.time ? `, ${data.time}` : ''})`
        : `Date changed to ${data.date}${data.time ? ` ${data.time}` : ''}`, { duration: 7000 });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change the date');
    }
  };
  // 2026-10-19 (round 5) — rename the opponent. The use case is a tournament
  // or final scheduled as "TBA"/"TBD": once the bracket settles, fix it here.
  const editOpponent = async () => {
    setOpen(false);
    const name = window.prompt('Who is the opponent?', game.opponent && !/^\s*(tba|tbd|tbc)\b/i.test(game.opponent) ? game.opponent : '');
    if (name == null) return;
    const trimmed = name.trim().slice(0, 120);
    if (!trimmed) { toast.error('The opponent needs a name'); return; }
    try {
      await api.patch(`/teams/${teamId}/press-box/games/${game.id}`, { opponent: trimmed });
      toast.success(`Now vs ${trimmed}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save the opponent');
    }
  };
  // 2026-10-26 (per Gilles) — re-run the AI straight from the list (published
  // games too), e.g. after a model improvement. Hand edits and an applied
  // official score sheet are kept; a published family page shows the update.
  const rerunAi = async () => {
    setOpen(false);
    if (!window.confirm(`Run ${PRESS_BOX.AI_NAME} over this game's transcript again?\n\nYour hand edits and an applied official score sheet are kept.${game.publishedAt ? ' The published family page shows the updated stats.' : ''}`)) return;
    try {
      await api.post(`/teams/${teamId}/press-box/games/${game.id}/reanalyze`, { recordingId: game.recording?.id, analysis: 'ai' });
      toast.success(`${PRESS_BOX.AI_NAME} is re-reading the game — the stats update in a few minutes`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not start the re-run');
    }
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="btn-ghost" style={{ padding: '6px 8px' }} aria-label="Game options" onClick={() => setOpen(o => !o)}>
        <MoreVertical size={14} />
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 30, minWidth: 170,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
          {game.recording && game.recording.engine === 'final' && (
            <button type="button" onClick={rerunAi} data-menu-rerun className="w-full text-left flex items-center gap-2 text-sm"
              style={{ padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)' }}>
              <RefreshCw size={13} /> Re-run {PRESS_BOX.AI_NAME}…
            </button>
          )}
          <button type="button" onClick={changeDate} className="w-full text-left flex items-center gap-2 text-sm"
            style={{ padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)' }}>
            <Pencil size={13} /> Change date / time…
          </button>
          <button type="button" onClick={editOpponent} className="w-full text-left flex items-center gap-2 text-sm"
            style={{ background: 'transparent', border: 'none', color: 'var(--text)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>
            <Pencil size={13} /> Edit opponent…
          </button>
          {/* 2026-10-27 (per Gilles) — a tournament game: the league calendar
              never posts its final, so the card can say so instead of waiting. */}
          {!game.tournament ? (
            <button type="button" data-menu-tournament className="w-full text-left flex items-center gap-2 text-sm"
              style={{ background: 'transparent', border: 'none', color: 'var(--text)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}
              onClick={async () => {
                const name = window.prompt('This marks the game as a TOURNAMENT game (the league calendar never posts tournament finals).\n\nTournament name (as it should show):', 'Tournament');
                if (name === null) return;
                try {
                  await api.patch(`/teams/${teamId}/press-box/games/${game.id}`, { tournament: name.trim() || true });
                  toast.success('Marked as a tournament game');
                  onChanged?.();
                } catch (e) { toast.error(e.response?.data?.error || 'Could not save that'); }
              }}>
              <Pencil size={13} /> Mark as tournament game…
            </button>
          ) : (
            <button type="button" data-menu-tournament className="w-full text-left flex items-center gap-2 text-sm"
              style={{ background: 'transparent', border: 'none', color: 'var(--text)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}
              onClick={async () => {
                if (!window.confirm(`Remove the tournament mark (${game.tournament})?`)) return;
                try {
                  await api.patch(`/teams/${teamId}/press-box/games/${game.id}`, { tournament: null });
                  toast.success('No longer marked as a tournament game');
                  onChanged?.();
                } catch (e) { toast.error(e.response?.data?.error || 'Could not save that'); }
              }}>
              <Pencil size={13} /> Not a tournament game ({game.tournament})
            </button>
          )}
          <button type="button" onClick={del} className="w-full text-left flex items-center gap-2 text-sm"
            style={{ background: 'transparent', border: 'none', color: 'var(--penalty)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>
            <Trash2 size={13} /> Delete this game…
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 2026-10-19 (round 5, per Gilles) — a season's worth of tapes at once: pick
 * up to 25 audio files and Press Box works out which game each one is. The
 * file's own date stamp names the day (a voice memo is saved right after the
 * final buzzer); a scheduled game that day without a recording takes the
 * file, and a day with no game gets one (vs TBA — after the pass the tape's
 * spoken date, opponent and final score refine it). Files go up one at a
 * time, and each pass queues behind the GPU like any other: when a higher-
 * priority job holds the card, the analysis waits and comes back on its own.
 */
function BulkUploadCard({ teamId, onDone }) {
  const inputRef = useRef(null);
  const [plan, setPlan] = useState(null);          // [{name, action, gameId?, date, time, opponent?, why?}]
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState([]);          // per-file { status, pct, error }
  const [running, setRunning] = useState(false);
  const [matching, setMatching] = useState(false);

  const pick = async (list) => {
    const fs = [...(list || [])].slice(0, 25);
    if (!fs.length) return;
    if ((list || []).length > 25) toast('25 files at a time — the first 25 are in', { icon: 'ℹ️' });
    setMatching(true); setPlan(null); setItems([]);
    try {
      // 2026-10-21 — hash each file first so the plan can say "already
      // uploaded" and nothing is ever analysed twice.
      const shas = await Promise.all(fs.map(f => fileSha256(f)));
      const { data } = await api.post(`/teams/${teamId}/press-box/uploads/match`, {
        files: fs.map((f, i) => ({ name: f.name, lastModified: f.lastModified, durationMs: null, sha256: shas[i] || undefined })),
      });
      setFiles(fs.map((f, i) => Object.assign(f, { _sha: shas[i] || null })));
      setPlan(data.plan || []);
      setItems((data.plan || []).map(pl => ({ status: pl.action === 'duplicate' ? 'duplicate' : 'queued', pct: 0 })));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not match the files to games');
    } finally { setMatching(false); }
  };

  const start = async () => {
    setRunning(true);
    const setItem = (i, patch) => setItems(arr => arr.map((x, j) => j === i ? { ...x, ...patch } : x));
    for (let i = 0; i < files.length; i++) {
      const p = plan[i];
      if (p.action === 'duplicate') continue;          // already uploaded — never re-process
      try {
        let gameId = p.action === 'attach' ? p.gameId : null;
        if (!gameId) {
          setItem(i, { status: 'creating game' });
          const { data: g } = await api.post(`/teams/${teamId}/press-box/games`,
            { date: p.date || new Date().toISOString().slice(0, 10), time: p.time || '12:00', opponent: 'TBA' });
          gameId = g.gameId;
        }
        setItem(i, { status: 'sending' });
        await uploadRecording({ teamId, gameId, file: files[i], sha256: files[i]._sha,
          onProgress: (pr) => setItem(i, { pct: pr.pct, status: pr.phase === 'processing' ? 'processing' : 'sending' }) });
        // uploaded + matched: say where it landed and that the analysis is on
        setItem(i, { status: 'analysing', pct: 100, gameId });
      } catch (e) {
        if (e.response?.data?.error === 'duplicate_recording') {
          setItem(i, { status: 'duplicate', error: e.response.data.message });
        } else {
          setItem(i, { status: 'failed', error: uploadError(e) });
        }
      }
    }
    setRunning(false);
    toast.success('Uploads done — each game below shows its analysis progress (the AI waits its turn when the GPU is busy)', { duration: 9000 });
    onDone?.();
  };

  const reset = () => { setPlan(null); setFiles([]); setItems([]); if (inputRef.current) inputRef.current.value = ''; };
  const ICON = { queued: '·', 'creating game': '…', sending: '↑', processing: '…', done: '✓', analysing: '✓', duplicate: '≡', failed: '✗' };

  return (
    <details className="card mb-4" data-bulk-upload>
      <summary className="font-bold text-sm flex items-center gap-2" style={{ cursor: 'pointer', listStyle: 'none' }}>
        <Upload size={14} /> Bulk upload — up to 25 recordings at once
      </summary>
      <div className="mt-2">
        <div className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>
          Press Box reads each file&rsquo;s date stamp to find its game on the schedule; a day with no game gets one
          (vs TBA — the tape itself then names the opponent). Files go up one at a time, and each analysis waits its
          turn when something more important holds the GPU, so a big batch can take a while. Leave the page open until
          the uploads finish; the analyses carry on by themselves.
        </div>
        {!plan && (
          <input ref={inputRef} type="file" multiple accept={RECORDING_ACCEPT} className="input-field" style={{ padding: 9 }}
            disabled={matching} onChange={(e) => pick(e.target.files)} />
        )}
        {matching && <div className="text-sm mt-2" style={{ color: 'var(--text-2)' }}><Loader2 size={13} className="animate-spin" style={{ display: 'inline' }} /> Matching the files to games…</div>}
        {plan && (
          <div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {plan.map((p, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs" style={{ borderBottom: i < plan.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', width: 14, textAlign: 'center', color: items[i]?.status === 'failed' ? 'var(--penalty)' : ['done', 'analysing'].includes(items[i]?.status) ? 'var(--goal)' : items[i]?.status === 'duplicate' ? 'var(--gold)' : 'var(--text-3)' }}>{ICON[items[i]?.status] || '·'}</span>
                  <span className="flex-1" style={{ minWidth: 160, color: 'var(--text)', overflowWrap: 'anywhere' }}>{p.name}</span>
                  <span style={{ color: 'var(--text-2)' }}>
                    {p.action === 'duplicate'
                      ? <span style={{ color: 'var(--gold)' }}>already uploaded — {p.date}{p.opponent ? ` vs ${p.opponent}` : ''} (skipped, not re-processed)</span>
                      : p.action === 'attach'
                        ? `→ ${p.date}${p.opponent ? ` vs ${p.opponent}` : ''}`
                        : p.provisional
                          ? <>→ stand-in game vs TBA — <span style={{ color: 'var(--gold)' }}>date unknown for now: {p.why}</span></>
                          : `→ new game ${p.date || 'today'}${p.time ? ` ${p.time}` : ''} vs TBA (${p.why})`}
                  </span>
                  {items[i]?.status === 'sending' && <span style={{ color: 'var(--ice)', fontFamily: 'var(--font-mono)' }}>{items[i].pct}%</span>}
                  {items[i]?.status === 'analysing' && (
                    <Link to={`/press-box/${teamId}/game/${items[i].gameId}`} style={{ color: 'var(--ice)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      analysing — open the game →
                    </Link>
                  )}
                  {items[i]?.status === 'duplicate' && items[i]?.error && <span style={{ color: 'var(--gold)' }}>{items[i].error}</span>}
                  {items[i]?.status === 'failed' && <span style={{ color: 'var(--penalty)' }}>{items[i].error}</span>}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              {items.every(x => ['done', 'analysing', 'duplicate', 'failed'].includes(x.status)) && items.length ? (
                <button type="button" className="btn-ice" onClick={reset}><CheckCircle2 size={13} /> Done — pick more</button>
              ) : (
                <>
                  <button type="button" className="btn-ice" onClick={start} disabled={running}>
                    {running ? <><Loader2 size={13} className="animate-spin" /> Uploading…</> : `Upload ${files.length} file${files.length === 1 ? '' : 's'}`}
                  </button>
                  {!running && <button type="button" className="btn-ghost" onClick={reset}>Cancel</button>}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}

export function PressBoxIndex() {
  const { user } = useAuth();
  const { teams = [], teamsLoaded, activeTeam } = useOutletContext() || {};
  if (!teamsLoaded && !teams.length) return <div className="text-secondary text-sm p-4">Loading…</div>;
  const mine = teams.filter(t => keepsStats(t, user?.id));
  const pick = (activeTeam && keepsStats(activeTeam, user?.id)) ? activeTeam : mine[0];
  if (pick) return <Navigate to={`/press-box/${pick.id}`} replace />;
  return (
    <PressBoxShell userId={user?.id}>
      <div className="card text-center p-4">
        <div className="font-bold mb-1">No team to keep stats for</div>
        <div className="text-secondary text-sm">Press Box is for the coach, managers and the team's designated stat keepers.</div>
      </div>
    </PressBoxShell>
  );
}

export default function PressBoxGamesPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [season, setSeason] = useState('');
  const [ai, setAi] = useState(null);
  // gameId -> { pct, phase, message } — mirrored in a store outside the page, so coming
  // back to this page mid-upload shows the upload again (2026-11-02, per Gilles)
  const [uploading, setUploadingState] = useState(() => activeUploads.get());
  useEffect(() => activeUploads.subscribe(setUploadingState), []);
  const setUploading = useCallback((fn) => {
    const next = typeof fn === 'function' ? fn(activeUploads.get()) : fn;
    const cur = activeUploads.get();
    for (const k of new Set([...Object.keys(cur), ...Object.keys(next || {})])) {
      if (JSON.stringify(cur[k]) !== JSON.stringify(next?.[k])) activeUploads.set(k, next?.[k] || null);
    }
  }, []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: '', time: '19:00', opponent: '' });
  const fileRefs = useRef({});
  // 2026-10-24 (per Gilles) — Upload offers the recording OR the official score sheet
  const sheetRefs = useRef({});
  const videoRefs = useRef({});
  const anyRefs = useRef({});
  // 2026-10-26 (per Gilles) — one picker for everything: images become the
  // official score sheet, a file with a picture becomes the game video, the
  // rest is the narration recording. (A "video/mp4" with no picture is an
  // iPhone voice memo: narration.)
  const uploadAny = async (gameId, fileList) => {
    const files = [...fileList];
    const images = files.filter(f => /^image\//.test(f.type));
    const rest = files.filter(f => !/^image\//.test(f.type));
    const videos = []; const audios = [];
    if (rest.length) toast(`Checking ${rest.length} file(s)…`, { duration: 4000 });
    for (const f of rest) {
      const probe = await probeFile(f);
      const ext = String(f.name || '').split('.').pop()?.toLowerCase();
      // 2026-11-02 (per Gilles: "nothing happened"): a 900 MB camera file keeps the
      // browser's probe silent; then the extension and the size decide — an iPhone
      // voice memo is an .m4a or a small .mp4, a camera file is a big .mp4/.mov
      const bigVideoFile = ['mp4', 'mov', 'mkv', 'm4v', '3gp'].includes(ext) && f.size > 150 * 1024 * 1024;
      const isVideo = probe.video === true || (probe.video === null && (bigVideoFile || (/^video\//.test(f.type) && f.type !== 'video/mp4')));
      (isVideo ? videos : audios).push(f);
    }
    if (!images.length && !videos.length && !audios.length) { toast.error('Pick the narration audio, the game video, or score-sheet screenshots'); return; }
    if (audios.length > 5) { toast.error('Up to 5 audio parts for one game — more than that, use Bulk upload (one file per game)'); return; }
    if (videos.length > 4) { toast.error('Up to 4 video segments for one game'); return; }
    const plan = [audios.length && (audios.length > 1 ? `narration (${audios.length} parts, stitched)` : 'narration'),
      videos.length && (videos.length > 1 ? `game video (${videos.length} segments, joined)` : 'game video'),
      images.length && `score sheet (${images.length})`].filter(Boolean).join(' + ');
    toast.success(`Uploading: ${plan}`);
    if (audios.length > 1) await uploadAudioParts(gameId, audios);
    else if (audios.length) await upload(gameId, audios[0]);     // starts the stats pass
    if (videos.length) await uploadVideoParts(gameId, videos, { stay: !!audios.length });
    if (images.length) await uploadSheet(gameId, images, { stay: !!audios.length || !!videos.length });
  };
  const uploadVideo = async (gameId, file, { stay = false } = {}) => uploadVideoParts(gameId, [file].filter(Boolean), { stay });
  // 2026-10-28 (per Gilles) — the arena camera splits a game into ~30-min
  // files: several segments go up oldest-first (the files' own modified
  // times) and are joined into one video; clips are cut after the last.
  const uploadVideoParts = async (gameId, files, { stay = false } = {}) => {
    const parts = [...files].sort((a, b) => (a.lastModified || 0) - (b.lastModified || 0));
    if (!parts.length) return;
    if (parts.length > 1) toast.success(`Joining ${parts.length} video segments into one game video, in recorded order`, { duration: 8000 });
    setUploading(u => ({ ...u, [gameId]: { phase: 'sending', pct: 0, message: 'Uploading the video…' } }));
    try {
      for (let i = 0; i < parts.length; i++) {
        const seg = parts.length > 1 ? ` (segment ${i + 1}/${parts.length})` : '';
        await uploadGameVideo({ teamId, gameId, file: parts[i], append: i > 0, more: i < parts.length - 1,
          onProgress: (p) => setUploading(u => ({ ...u, [gameId]: { phase: 'sending', pct: p.pct, message: `Uploading the video${seg} ${p.pct}%` } })) });
      }
      toast.success('Video uploaded — joining & storing now. On the game page: set where OUR game starts and which shirts we wore.', { duration: 9000 });
      if (!stay) navigate(`/press-box/${teamId}/game/${gameId}`);
    } catch (e) { toast.error(uploadError(e)); }
    finally { setUploading(u => { const n = { ...u }; delete n[gameId]; return n; }); }
  };
  const [uploadMenu, setUploadMenu] = useState(null);
  const appendRefs = useRef({});
  const [appendPick, setAppendPick] = useState(null);   // { gameId, recordingId, file }
  useEffect(() => {
    if (!uploadMenu) return undefined;
    const close = (e) => { if (!e.target.closest?.('[data-upload-menu]')) setUploadMenu(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [uploadMenu]);
  const uploadSheet = async (gameId, files, { stay = false } = {}) => {
    setUploading(u => ({ ...u, [gameId]: { phase: 'sheet', message: 'Sending the score sheet…' } }));
    try {
      await uploadScoreSheet(teamId, gameId, files);
      toast.success('Score sheet uploaded — the stats AI reads it; review and apply it on the game page');
      if (!stay) navigate(`/press-box/${teamId}/game/${gameId}`);
    } catch (e) { toast.error(e.response?.data?.error || e.message || 'Could not upload the screenshots'); }
    finally { setUploading(u => { const n = { ...u }; delete n[gameId]; return n; }); }
  };
  const addFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/teams/${teamId}/press-box`, { params: season ? { season } : {} });
      setData(d);
    } catch (e) {
      setData({ error: e.response?.status === 404 ? 'off' : (e.response?.data?.error || 'Could not load Press Box') });
    }
  }, [teamId, season]);
  useEffect(() => { load(); }, [load]);
  // A keeper of two teams: the season picked on one means nothing on the other.
  useEffect(() => { setSeason(''); }, [teamId]);
  useEffect(() => {
    let c = false;
    api.get(`/teams/${teamId}/press-box/ai-status`, { timeout: 25000 }).then(({ data: d }) => { if (!c) setAi(d); }).catch(() => {});
    return () => { c = true; };
  }, [teamId]);
  useEffect(() => {
    chatSocket.joinTeam(teamId);
    const offs = [
      chatSocket.on('GAME_STATS_FINAL', (d) => { setUploading(u => { const n = { ...u }; delete n[d?.gameId]; return n; }); load(); }),
      chatSocket.on('GAME_STATS_ERROR', (d) => { setUploading(u => { const n = { ...u }; delete n[d?.gameId]; return n; }); load(); }),
      chatSocket.on('GAME_STATS_PROGRESS', (d) => { if (d?.gameId) setUploading(u => ({ ...u, [d.gameId]: { ...(u[d.gameId] || {}), phase: 'processing', message: d.message } })); }),
      chatSocket.on('GAME_STATS_GOAL', () => load()),
    ];
    return () => offs.forEach(f => f?.());
  }, [teamId, load]);
  // While anything is being recorded or analysed, look again every 30 s: the
  // socket can miss the end (a phone asleep, a socket that gave up).
  const busy = !!data?.games?.some(g => g.live || IN_PROGRESS.includes(g.recording?.engine)
    || g.video?.status === 'storing' || ['queued', 'building'].includes(g.video?.clipsStatus));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [busy, load]);

  const upload = async (gameId, file, { appendTo = null, position = 'after', analyze = true, quiet = false } = {}) => {
    if (!file) return null;
    try {
      const { video, recordingId } = await uploadRecording({ teamId, gameId, file, appendTo, position, analyze,
        onProgress: (p) => setUploading(u => ({ ...u, [gameId]: { ...(u[gameId] || {}), ...p } })) });
      if (!quiet) {
        toast.success(appendTo
          ? 'Extra audio added — stitching it on, then the stats run over the whole tape'
          : (video ? 'Video uploaded — pulling the sound out, then the stats' : 'Recording uploaded — the stats follow in a few minutes'), { duration: 7000 });
      }
      load();
      return recordingId;
    } catch (e) {
      setUploading(u => { const n = { ...u }; delete n[gameId]; return n; });
      toast.error(uploadError(e), { duration: 8000 });
      return null;
    }
  };
  // 2026-10-27 (per Gilles) — "my recorder stopped working halfway and I had
  // to start a new one": the picker takes SEVERAL audio files and they are
  // stitched into one tape, oldest first (the files' own modified times say
  // which part was recorded first). A later extra part is added from the
  // game page's "Add more audio".
  const uploadAudioParts = async (gameId, audios) => {
    const parts = [...audios].sort((a, b) => (a.lastModified || 0) - (b.lastModified || 0));
    toast.success(`Stitching ${parts.length} audio files into one tape, in recorded order`, { duration: 8000 });
    const recordingId = await upload(gameId, parts[0], { analyze: false, quiet: true });
    if (!recordingId) return;
    for (let i = 1; i < parts.length; i++) {
      const last = i === parts.length - 1;
      await upload(gameId, parts[i], { appendTo: recordingId, position: 'after', analyze: last, quiet: !last });
    }
  };

  const addGame = async (e) => {
    e?.preventDefault?.();
    const file = addFileRef.current?.files?.[0] || null;
    if (!form.date || !form.opponent.trim()) { toast.error('Date and opponent, please'); return; }
    setAdding(true);
    try {
      const { data: g } = await api.post(`/teams/${teamId}/press-box/games`, form);
      setForm({ date: '', time: '19:00', opponent: '' });
      await load();
      if (file) { if (addFileRef.current) addFileRef.current.value = ''; await upload(g.gameId, file); }
      else toast.success('Game added — upload its recording from the list');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add the game');
    } finally { setAdding(false); }
  };

  if (!data) return <PressBoxShell teamId={teamId} userId={user?.id}><div className="text-secondary text-sm p-4">Loading…</div></PressBoxShell>;
  if (data.error) {
    return (
      <PressBoxShell teamId={teamId} userId={user?.id}>
        <div className="card text-center p-4">
          <div className="font-bold mb-1">{data.error === 'off' ? 'Press Box is not switched on for your account' : data.error}</div>
          {data.error === 'off' && <div className="text-secondary text-sm">The super admin turns it on per user (Admin → user → Feature flags → Press Box).</div>}
        </div>
      </PressBoxShell>
    );
  }

  const rec = data.record || { w: 0, l: 0, t: 0, gf: 0, ga: 0 };
  const games = data.games || [];
  const canAdd = ['coach', 'manager'].includes(data.role);
  // Recording and uploading are the coach's and the named recorders' (the
  // server refuses anyone else); managers read and fix the stats.
  const canRecord = !!data.canRecord;
  const aiLine = (() => {
    if (!data.settings?.aiAnalysis) return { color: 'var(--text-2)', text: 'AI analysis is off for this team — stats come from the rules only.', link: true };
    if (!ai) return { color: 'var(--text-2)', text: 'Checking the stats AI…' };
    if (ai.engine === false) return { color: 'var(--puck)', text: 'Transcription is not turned on on this server.' };
    if (ai.error) return { color: 'var(--puck)', text: ai.error };
    if (!ai.enabled) return { color: 'var(--puck)', text: `AI analysis is on for this team, but no model is set up on the server yet${ai.admin ? ' (Super admin → Stats AI)' : ''}. Games get rules only until then.` };
    if (ai.ping?.busy) return { color: 'var(--puck)', text: `The stats AI's GPU is busy right now (a training run): games get the rules' result first and ${modelAlias(ai.endpoint?.model) || 'the stats AI'} checks them when the card is free.` };
    if (ai.ping && !ai.ping.ok) return { color: 'var(--penalty)', text: `${modelAlias(ai.endpoint?.model) || 'The stats AI'} is not answering${ai.admin && ai.ping.error ? `: ${ai.ping.error}` : ''}. Games fall back to the rules until it does.` };
    const warm = Array.isArray(ai.ping?.loaded) ? ai.ping.loaded.length > 0 : !!ai.ping?.loaded;
    return { color: 'var(--goal)', text: `After every game the transcript is checked by ${modelAlias(ai.endpoint?.model) || 'the stats AI'}${warm ? ' (loaded and warm)' : ''}.` };
  })();

  return (
    <PressBoxShell teamId={teamId} userId={user?.id}>
      {/* 2026-10-19 — a standalone team without a roster yet: the stats land
          on bare numbers until the jerseys are in, so nudge before the first
          recording. */}
      {data.team?.type === 'press_box' && data.rosterCount === 0 && data.pbRole === 'admin' && (
        <div className="card mb-4 flex items-center gap-3" style={{ border: '1px solid var(--gold)', flexWrap: 'wrap' }} data-roster-nudge>
          <Users size={18} style={{ color: 'var(--gold)', flexShrink: 0 }} />
          <div className="flex-1 text-sm" style={{ minWidth: 220, color: 'var(--text-2)' }}>
            <strong style={{ color: 'var(--text)' }}>Set up your roster.</strong>{' '}
            Add your players&rsquo; jersey numbers (snap a photo of the team list — the AI reads it) so every
            &ldquo;goal, number 19&rdquo; lands on the right player instead of a bare number.
          </div>
          <Link to={`/press-box/${teamId}/roster`} className="btn-ice" style={{ textDecoration: 'none' }}>Set up the roster</Link>
        </div>
      )}

      {/* season + record */}
      <div className="card mb-4">
        <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
          <div className="flex items-baseline gap-4" style={{ flexWrap: 'wrap' }}>
            <div><div className="label">Record</div><div className="display-num" style={{ fontSize: 28 }}>{rec.w}–{rec.l}–{rec.t}</div></div>
            <div><div className="label">Goals for</div><div className="display-num" style={{ fontSize: 28, color: 'var(--goal)' }}>{rec.gf}</div></div>
            <div><div className="label">Against</div><div className="display-num" style={{ fontSize: 28, color: 'var(--penalty)' }}>{rec.ga}</div></div>
          </div>
          {/* 2026-10-22 (per Gilles) — one switch for all GPU work: pause
              holds every game (and stops running passes); resume lets the
              5-minute sweep re-book what is still unfinished. */}
          {canAdd && games.some(g => g.recording?.aiQueued || g.recording?.aiHold || IN_PROGRESS.includes(g.recording?.engine)) && (
            games.some(g => g.recording?.aiQueued || IN_PROGRESS.includes(g.recording?.engine)) ? (
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px', color: 'var(--penalty)' }}
                onClick={async () => {
                  if (!window.confirm('Pause ALL analysis for this team?\n\nRunning passes wind down, queued games go on hold, and nothing goes to the GPU until you resume (or Re-run a single game).')) return;
                  try { const { data: r } = await api.post(`/teams/${teamId}/press-box/ai-queue/pause`); toast.success(`Paused — ${r.held} game(s) on hold${r.stopped ? `, ${r.stopped} running pass(es) winding down` : ''}`); load(); }
                  catch (e) { toast.error(e.response?.data?.error || 'Could not pause'); }
                }}>
                <Square size={13} /> Pause all analysis
              </button>
            ) : (
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }}
                onClick={async () => {
                  try { const { data: r } = await api.post(`/teams/${teamId}/press-box/ai-queue/resume`); toast.success(`Resumed — ${r.released} game(s) back in the queue (picked up within ~5 min)`); load(); }
                  catch (e) { toast.error(e.response?.data?.error || 'Could not resume'); }
                }}>
                <RefreshCw size={13} /> Resume the queue
              </button>
            )
          )}
          {data.seasons?.length > 0 && (
            <select className="input-field" style={{ width: 'auto', padding: '8px 12px' }} value={season || data.seasonId || 'all'} onChange={(e) => setSeason(e.target.value)} aria-label="Season">
              {data.seasons.map(s => <option key={s.id} value={s.id}>{s.name}{s.active ? ' (current)' : ''}</option>)}
              <option value="all">All seasons</option>
            </select>
          )}
        </div>
        <div className="flex items-start gap-2 text-xs mt-3" style={{ color: aiLine.color }}>
          <Sparkles size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{aiLine.text}{aiLine.link && <> <Link to={`/press-box/${teamId}/settings`} style={{ color: 'var(--ice)' }}>Turn it on</Link></>}</span>
        </div>
      </div>

      {/* add a game + upload — folded away once there are games, so the list comes first on a phone */}
      {canAdd && (
        <details className="card mb-4" open={games.length === 0}>
          <summary className="font-bold text-sm flex items-center gap-2" style={{ cursor: 'pointer', listStyle: 'none' }}><Plus size={14} /> {canRecord ? 'Add a game and upload its recording' : 'Add a game'}</summary>
          <form onSubmit={addGame} className="mt-2">
          <div className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>
            {canRecord
              ? 'For a game that isn\'t on the schedule. Audio (voice memo, WhatsApp audio, m4a, mp3, wav) or a video — only the sound is kept. Games already on the schedule upload from their row below.'
              : 'For a game that isn\'t on the schedule. Recordings are uploaded by the coach or a designated stat recorder (Team Settings → Live stats — who can record).'}
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <label><span className="label">Date</span><input type="date" className="input-field" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required /></label>
            <label><span className="label">Time</span><input type="time" className="input-field" value={form.time} onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))} /></label>
            <label><span className="label">Opponent</span><input className="input-field" placeholder="Guelph Gryphons" maxLength={120} value={form.opponent} onChange={(e) => setForm(f => ({ ...f, opponent: e.target.value }))} required /></label>
            {canRecord && <label><span className="label">Recording (optional)</span><input ref={addFileRef} type="file" accept={RECORDING_ACCEPT} className="input-field" style={{ padding: 9 }} /></label>}
          </div>
          <div className="mt-3"><button type="submit" className="btn-ice" disabled={adding}>{adding ? 'Adding…' : 'Add game'}</button></div>
          </form>
        </details>
      )}

      {canAdd && canRecord && <BulkUploadCard teamId={teamId} onDone={load} />}

      {/* 2026-10-19 (round 5, per Gilles) — how to narrate, right where the
          recordings are made and uploaded. */}
      {canRecord && (
        <details className="card mb-4" data-narrator-help>
          <summary className="font-bold text-sm flex items-center gap-2" style={{ cursor: 'pointer', listStyle: 'none' }}>
            <Mic size={14} /> How to narrate a game
          </summary>
          <div className="mt-2 text-sm" style={{ color: 'var(--text-2)', lineHeight: 1.6 }}>
            <div className="mb-2">Talk naturally — {PRESS_BOX.AI_NAME} reads the whole tape afterwards. A few habits make the stats much sharper:</div>
            <ul style={{ paddingLeft: 18, margin: 0, display: 'grid', gap: 6 }}>
              <li><strong style={{ color: 'var(--text)' }}>Open with the game:</strong> the date, time and who you&rsquo;re playing (&ldquo;Sunday, October 18th, 9 am, versus the Brantford 99ers&rdquo;). That&rsquo;s how a tape finds its game on the schedule.</li>
              <li><strong style={{ color: 'var(--text)' }}>Say who scored by the player&rsquo;s number:</strong> &ldquo;Goal, number 19&rdquo; — and assists the same way: &ldquo;assisted by 7 and 10&rdquo;.</li>
              <li><strong style={{ color: 'var(--text)' }}>Always narrate from your team&rsquo;s perspective.</strong> When the other team scores, just say so: &ldquo;the other team scored&rdquo; or &ldquo;&lt;their name&gt; scored&rdquo; — no numbers needed for them.</li>
              <li><strong style={{ color: 'var(--text)' }}>Say the score after every goal</strong> (&ldquo;2–1 for us&rdquo;). Spoken scores are checkpoints: they catch missed and phantom goals.</li>
              <li><strong style={{ color: 'var(--text)' }}>Shots and faceoffs:</strong> &ldquo;shot on net by number 4&rdquo;, &ldquo;faceoff won by 9&rdquo;, &ldquo;faceoff lost&rdquo;. Each call counts as one. No number handy? Say it anyway — it still counts, under &ldquo;Unknown&rdquo; in the tables.</li>
              <li><strong style={{ color: 'var(--text)' }}>Corrections are heard:</strong> &ldquo;no goal&rdquo;, &ldquo;that one was called back&rdquo;, &ldquo;doesn&rsquo;t count&rdquo; cancel the call they follow.</li>
              <li><strong style={{ color: 'var(--text)' }}>Penalties and saves:</strong> &ldquo;penalty on number 44, two minutes&rdquo;, &ldquo;big save by our goalie&rdquo;.</li>
              <li><strong style={{ color: 'var(--text)' }}>The goalie — name them once:</strong> say the number or name on the first save (&ldquo;saved by number 31&rdquo;, &ldquo;saved by our goalie Greyson&rdquo;). After that, &ldquo;saved by the goalie&rdquo; or &ldquo;saved by the Rangers goalie&rdquo; is enough — every save and goal against goes to that goalie, who is assumed in net all game. Only call out a <em>goalie change</em> if one happens.</li>
              <li><strong style={{ color: 'var(--text)' }}>Close with the final:</strong> &ldquo;game over, final score 3–1 for us&rdquo;.</li>
            </ul>
          </div>
        </details>
      )}

      {/* games */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {games.length === 0 && <div className="text-secondary text-sm p-4 text-center">No games in this season yet.</div>}
        {games.map((g, gi) => {
          const up = uploading[g.id];
          // Season phases (set in Site & Settings) appear as section headers.
          const ph = phaseOf(g.date, data.phases);
          const prevPh = gi > 0 ? phaseOf(games[gi - 1].date, data.phases) : undefined;
          const header = ph && ph !== prevPh ? (
            <div key={`ph-${g.id}`} className="px-3 py-1.5 text-xs font-bold" style={{ color: 'var(--gold)', background: 'var(--surface-2)', letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>
              {PHASE_LABELS[ph]}
            </div>
          ) : null;
          return (
            <React.Fragment key={g.id}>
            {header}
            {/* 2026-10-21 (per Gilles) — the whole row opens the game; buttons,
                links and the ⋯ menu keep their own jobs. */}
            <div className="flex items-center gap-3 px-3 py-3" data-game-row={g.id}
              style={{ borderBottom: '1px solid var(--border)', flexWrap: 'wrap', cursor: 'pointer' }}
              onClick={(e) => { if (e.target.closest('button, a, input, label, [role="menu"]')) return; navigate(`/press-box/${teamId}/game/${g.id}`); }}>
              <div style={{ minWidth: 92 }}>
                <div className="text-sm font-semibold">{fmtDate(g.date)}</div>
                <div className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtGameTime(g.time, data.timeFormat)}</div>
              </div>
              <div className="flex-1 min-w-0" style={{ minWidth: 160 }}>
                <Link to={`/press-box/${teamId}/game/${g.id}`} className="text-sm font-bold" style={{ color: 'var(--text)', textDecoration: 'none' }}>
                  vs {g.opponent || 'opponent not set'}
                </Link>
                {g.location && (
                  <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                    {g.mapQuery ? (
                      <a href={mapsUrl(g.mapQuery)} target="_blank" rel="noreferrer" title="Open in Google Maps"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'var(--text-3)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, width: 'fit-content' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ice)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}>
                        <MapPin size={11} style={{ verticalAlign: '-1px' }} /> {g.location}
                      </a>
                    ) : <><MapPin size={11} style={{ verticalAlign: '-1px' }} /> {g.location}</>}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1" style={{ flexWrap: 'wrap' }}>
                  <StatusChip game={g} />
                  {g.needsReview > 0 && <span className="badge" style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)' }}><AlertTriangle size={11} /> {g.needsReview} to check</span>}
                  {g.scoreMismatch && <span className="badge" title={`Unreconciled goals: the league's final is ${g.scoreMismatch.league.us}–${g.scoreMismatch.league.them}, but the recording counted ${g.scoreMismatch.tape.us}–${g.scoreMismatch.tape.them}. Open the game to listen and reconcile.`}
                    style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)' }}><AlertTriangle size={11} /> unreconciled goals</span>}
                  {/* 2026-10-24 (per Gilles) — the official sheet, and the webcal watch for the final */}
                  {g.sheet?.official && <span className="badge" title="The official score sheet is applied: our goals, assists and penalties are the sheet's"
                    style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)' }}><FileCheck2 size={11} /> official sheet</span>}
                  {g.sheet && !g.sheet.official && ['queued', 'reading', 'ready', 'failed'].includes(g.sheet.status) && (
                    <span className="badge" style={{ background: g.sheet.status === 'failed' ? 'var(--penalty-a10)' : 'var(--gold-a10)', color: g.sheet.status === 'failed' ? 'var(--penalty)' : 'var(--gold)', border: '1px solid var(--border)' }}>
                      <FileCheck2 size={11} /> {g.sheet.status === 'ready' ? 'score sheet to review' : g.sheet.status === 'failed' ? 'score sheet unreadable' : 'reading score sheet…'}
                    </span>)}
                  {g.scoreFeed?.state === 'waiting' && <span className="badge" title={`Checking the league webcal for the final every 30 minutes${g.scoreFeed.until ? ` until ${new Date(g.scoreFeed.until).toLocaleString()}` : ''}`}
                    style={{ background: 'var(--ice-a10)', color: 'var(--ice)', border: '1px solid var(--ice-a20)' }}><CalendarClock size={11} /> waiting for league score</span>}
                  {/* 2026-11-02 (per Gilles) — what is on file for the game, at a glance */}
                  {g.recording && !g.live && <span className="badge" data-audio-state title="Narration audio is on file for this game"
                    style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)' }}><Mic size={11} /> audio ✓</span>}
                  {/* 2026-10-28 (per Gilles) — the video's state on the row */}
                  {g.video && (g.video.status === 'storing'
                    ? <span className="badge" data-video-state style={{ background: 'var(--ice-a10, rgba(0,212,255,0.08))', color: 'var(--ice)', border: '1px solid var(--ice-a20, rgba(0,212,255,0.2))' }}>
                        <Loader2 size={11} className="animate-spin" /> video: joining &amp; storing…</span>
                    : ['queued', 'building'].includes(g.video.clipsStatus)
                    ? <span className="badge" data-video-state style={{ background: 'var(--ice-a10, rgba(0,212,255,0.08))', color: 'var(--ice)', border: '1px solid var(--ice-a20, rgba(0,212,255,0.2))' }}>
                        <Loader2 size={11} className="animate-spin" /> cutting goal clips…</span>
                    : g.video.needsSetup
                    ? <span className="badge" data-video-state title="Open the game: set where OUR game starts in the video (the camera caught the previous game/warmup) and which shirts we wore"
                        style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)', cursor: 'pointer' }}
                        onClick={() => navigate(`/press-box/${teamId}/game/${g.id}`)}>video needs setup →</span>
                    : <span className="badge" data-video-state title={`Game video stored${g.video.segments > 1 ? ` (${g.video.segments} segments joined)` : ''} — clips on the game page`}
                        style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)' }}>video ✓</span>)}
                  {g.tournament && <span className="badge" data-tournament title={`${g.tournament} — the league calendar never posts tournament finals; confirm the score in the game or upload the sheet`}
                    style={{ background: 'var(--ice-a10, rgba(0,212,255,0.08))', color: 'var(--ice)', border: '1px solid var(--ice-a20, rgba(0,212,255,0.2))' }}>{'\uD83C\uDFC6'} tournament</span>}
                  {g.scoreFeed?.state === 'gave_up' && <span className="badge" data-final-needed title={g.tournament
                    ? `Tournament game (${g.tournament}) — the league calendar never posts those finals; open the game to confirm the score`
                    : g.scoreFeed.note === 'no_entry'
                    ? 'This game is not on the league calendar (tournament) — open the game to confirm the final score'
                    : g.scoreFeed.note === 'no_score'
                      ? 'The league calendar lists this game without a score — open the game to confirm the final'
                      : 'No final on the league webcal after 3 days — open the game to confirm the final score'}
                    style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)' }}><AlertTriangle size={11} /> final score needed</span>}
                  {up && <span className="text-xs flex items-center gap-1" style={{ color: 'var(--ice)' }}><Loader2 size={12} className="animate-spin" /> {up.phase === 'sending' ? `Uploading ${up.pct || 0}%` : (up.message || 'Working out the stats…')}</span>}
                </div>
              </div>
              <div className="display-num" style={{ fontSize: 22, minWidth: 70, textAlign: 'right' }}>
                {g.score ? `${g.score.us}–${g.score.them}` : '–'}
                {g.result && <span className="text-xs" style={{ marginLeft: 6, color: g.result === 'W' ? 'var(--goal)' : (g.result === 'L' ? 'var(--penalty)' : 'var(--text-2)') }}>{g.result}</span>}
              </div>
              <div className="flex items-center gap-1">
                {canRecord && <>
                  <input ref={el => { fileRefs.current[g.id] = el; }} type="file" accept={RECORDING_ACCEPT} style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(g.id, f); }} />
                  <input ref={el => { sheetRefs.current[g.id] = el; }} type="file" accept={SHEET_ACCEPT} multiple style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files; e.target.value = ''; if (f?.length) uploadSheet(g.id, f); }} />
                  <input ref={el => { videoRefs.current[g.id] = el; }} type="file" accept={GAME_VIDEO_ACCEPT} multiple style={{ display: 'none' }}
                    onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ''; if (f.length) uploadVideoParts(g.id, f); }} />
                  <input ref={el => { anyRefs.current[g.id] = el; }} type="file" accept={ANY_ACCEPT} multiple style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files; e.target.value = ''; if (f?.length) uploadAny(g.id, f); }} />
                  <input ref={el => { appendRefs.current[g.id] = el; }} type="file" accept={RECORDING_ACCEPT} style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setAppendPick({ gameId: g.id, recordingId: g.recording?.id, file: f }); }} />
                  <div style={{ position: 'relative' }} data-upload-menu>
                    <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={!!up || g.live}
                      title="Upload the narration audio, the arena camera's video, score-sheet screenshots — or several at once; each file goes where it belongs"
                      aria-haspopup="menu"
                      onClick={() => {
                        const canSheet = ['admin', 'edit'].includes(data.pbRole);
                        if (!canSheet) { fileRefs.current[g.id]?.click(); return; }
                        setUploadMenu(m => (m === g.id ? null : g.id));
                      }}>
                      <Upload size={14} /> <span className="hidden sm:inline">Upload</span>
                    </button>
                    {uploadMenu === g.id && (
                      <div role="menu" className="card" style={{ position: 'absolute', right: 0, top: '110%', zIndex: 30, padding: 6, minWidth: 250, boxShadow: 'var(--shadow-lg)' }}>
                        {/* 2026-10-26 (per Gilles) — one choice for everything: pick any mix
                            of files and each goes where it belongs */}
                        <button type="button" role="menuitem" className="btn-ghost w-full" style={{ justifyContent: 'flex-start', padding: '8px 10px' }} data-upload-any
                          onClick={() => { setUploadMenu(null); anyRefs.current[g.id]?.click(); }}>
                          <Upload size={14} /> Audio + video together <span className="text-xs" style={{ color: 'var(--text-3)' }}>pick both files at once</span>
                        </button>
                        <button type="button" role="menuitem" className="btn-ghost w-full" style={{ justifyContent: 'flex-start', padding: '8px 10px' }}
                          onClick={() => { setUploadMenu(null); fileRefs.current[g.id]?.click(); }}>
                          <FileAudio size={14} /> Game recording <span className="text-xs" style={{ color: 'var(--text-3)' }}>narration audio</span>
                        </button>
                        {g.recording && !g.live && (
                          <button type="button" role="menuitem" className="btn-ghost w-full" style={{ justifyContent: 'flex-start', padding: '8px 10px' }} data-upload-append
                            onClick={() => { setUploadMenu(null); appendRefs.current[g.id]?.click(); }}>
                            <FileAudio size={14} /> Add more audio <span className="text-xs" style={{ color: 'var(--text-3)' }}>recorder died mid-game — stitch a part on</span>
                          </button>
                        )}
                        <button type="button" role="menuitem" className="btn-ghost w-full" style={{ justifyContent: 'flex-start', padding: '8px 10px' }} data-upload-sheet
                          onClick={() => { setUploadMenu(null); sheetRefs.current[g.id]?.click(); }}>
                          <FileCheck2 size={14} /> Official score sheet <span className="text-xs" style={{ color: 'var(--text-3)' }}>screenshots</span>
                        </button>
                        <button type="button" role="menuitem" className="btn-ghost w-full" style={{ justifyContent: 'flex-start', padding: '8px 10px' }} data-upload-video
                          onClick={() => { setUploadMenu(null); videoRefs.current[g.id]?.click(); }}>
                          <Clapperboard size={14} /> Game video <span className="text-xs" style={{ color: 'var(--text-3)' }}>arena camera — kept, goal clips cut</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <Link to={`/team/${teamId}/game/${g.id}/live`} className="btn-ghost" style={{ padding: '6px 10px' }} title="Record and track this game live">
                    <Mic size={14} />
                  </Link>
                </>}
                <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} onClick={() => navigate(`/press-box/${teamId}/game/${g.id}`)} title="Open the game">
                  <ChevronRight size={14} />
                </button>
                {data.pbRole === 'admin' && !g.live && <GameMenu teamId={teamId} game={g} onDeleted={load} onChanged={load} />}
              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>
      {appendPick && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setAppendPick(null); }}>
          <div className="card" style={{ maxWidth: 420, width: '100%', padding: 16 }} data-append-modal>
            <div className="font-bold mb-1">Add more audio to this game</div>
            <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
              <strong>{appendPick.file.name}</strong> will be stitched onto the existing recording as one tape, and the
              stats run again over the whole thing. Where does this part belong?
            </div>
            <div className="flex flex-col gap-2">
              <button type="button" className="btn-ice w-full" data-append-after
                onClick={() => { const { gameId, recordingId, file } = appendPick; setAppendPick(null); upload(gameId, file, { appendTo: recordingId, position: 'after' }); }}>
                After — it was recorded later (recorder died mid-game)
              </button>
              <button type="button" className="btn-ghost w-full" data-append-before
                onClick={() => { const { gameId, recordingId, file } = appendPick; setAppendPick(null); upload(gameId, file, { appendTo: recordingId, position: 'before' }); }}>
                Before — it is the start of the game
              </button>
              <button type="button" className="btn-ghost w-full" onClick={() => setAppendPick(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </PressBoxShell>
  );
}
